import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Modal, Input, Select, Spin, Alert } from "antd"

import Scheduling, { newRequestId } from "../../Apis/schedulingApi"
import {
  errorText,
  errorTextFrom,
  parseSchedulingError,
  formatCalendarDate,
  formatTime,
  formatTimeRange,
  timezoneOptions,
  zoneDateKey,
  zoneOffsetLabel,
} from "./schedulingUtils"
import "./Scheduling.css"

const { TextArea } = Input

/* Refusals that happen before any event could exist: only these may discard
   the attempt. Anything else — a timeout, a dropped connection, an answer we
   could not read — leaves the booking unresolved, so the dialog holds the
   frozen submission and reads it back instead of posting different details. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// Mirrors core.scheduling.constants.MAX_GUEST_EMAILS; the backend decides.
const GUEST_MAX = 5

/**
 * One readable line from a field error the backend reported.
 *
 * A list field answers with an object keyed by the offending position, which
 * React cannot render as a child, so every shape is flattened to text before
 * it reaches the form.
 */
const fieldErrorText = (value) => {
  if (value === null || value === undefined) return ""
  if (Array.isArray(value)) return value.map(fieldErrorText).filter(Boolean).join(" ")
  if (typeof value === "object") {
    return Object.values(value).map(fieldErrorText).filter(Boolean).join(" ")
  }
  return String(value)
}

const PRE_BOOKING_CODES = [
  "validation_error",
  "invalid_timezone",
  "scheduling_disabled",
  "calendar_unavailable",
  "calendar_reconnect_required",
  "rate_limited",
  "settings_changed",
  "slot_unavailable",
  "slot_expired",
  "slot_changed",
  "slot_busy",
  "request_conflict",
  "booking_failed",
]

/**
 * Admin booking. Same rules as the public modal: only a published, currently
 * free time can be chosen, there is no way to type an arbitrary time, and an
 * uncertain answer never turns into a second booking.
 */
const BookMeetingModal = ({ open, onClose, onBooked, t, lang, isRTL, organizerTimezone }) => {
  const [timezone, setTimezone] = useState(organizerTimezone || "UTC")
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [slots, setSlots] = useState([])
  const [loadError, setLoadError] = useState("")
  const [selectedDate, setSelectedDate] = useState("")
  const [selectedSlot, setSelectedSlot] = useState(null)

  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [message, setMessage] = useState("")
  const [guests, setGuests] = useState([])
  const [fieldErrors, setFieldErrors] = useState({})

  // Minted when an attempt starts, not while rendering, and reused by every
  // retry so a repeated submit maps to the original booking.
  const [requestId, setRequestId] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [pending, setPending] = useState(false)
  const [pendingBookingId, setPendingBookingId] = useState("")
  const [submitted, setSubmitted] = useState(null)
  const [submitError, setSubmitError] = useState("")

  const zones = useMemo(() => timezoneOptions(), [])
  const wasOpen = useRef(false)

  const loadSlots = useCallback(async () => {
    try {
      setLoadingSlots(true)
      setLoadError("")
      const response = await Scheduling.getSlots()
      const data = response?.data || {}
      const allSlots = data.slots || []
      if (data.calendar_status && data.calendar_status !== "ok" && allSlots.length) {
        setSlots([])
        setLoadError(errorText(t, { code: data.calendar_status }, t?.error_calendar_unavailable || "Calendar availability could not be checked. Try again."))
        return []
      }
      const rows = allSlots.filter((slot) => slot.state === "available")
      setSlots(rows)
      return rows
    } catch (error) {
      setSlots([])
      setLoadError(errorTextFrom(t, error, t?.error_load_slots || "Could not load times."))
      return []
    } finally {
      setLoadingSlots(false)
    }
  }, [t])

  useEffect(() => {
    if (!open) {
      wasOpen.current = false
      return
    }
    if (wasOpen.current) return
    wasOpen.current = true
    // Closing the dialog is not evidence that the external booking failed.
    // Reopening must keep its frozen payload and original recovery controls.
    if (pending || submitting) return
    setTimezone(organizerTimezone || "UTC")
    setRequestId("")
    setPending(false)
    setPendingBookingId("")
    setSubmitted(null)
    setSubmitError("")
    setFieldErrors({})
    setGuests([])
    loadSlots()
  }, [open, organizerTimezone, loadSlots, pending, submitting])

  // Slots grouped by the calendar day they fall on *in the chosen zone*, so
  // changing the zone regroups rather than relabels.
  const byDate = useMemo(() => {
    const groups = new Map()
    slots.forEach((slot) => {
      const key = zoneDateKey(slot.start_at, timezone)
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(slot)
    })
    return groups
  }, [slots, timezone])

  const dates = useMemo(() => Array.from(byDate.keys()).sort(), [byDate])

  useEffect(() => {
    if (!dates.length) {
      setSelectedDate("")
      return
    }
    if (!dates.includes(selectedDate)) setSelectedDate(dates[0])
  }, [dates, selectedDate])

  // Keep the selected slot by id while it is still offered.
  useEffect(() => {
    if (!selectedSlot) return
    if (!slots.some((slot) => slot.id === selectedSlot.id)) setSelectedSlot(null)
  }, [slots, selectedSlot])

  const daySlots = byDate.get(selectedDate) || []

  /**
   * What is wrong with the guest list as it stands, or "".
   *
   * Derived rather than stored: an entry the backend would refuse stays in the
   * list and visible, so it must be corrected or removed rather than silently
   * dropped on the way to a confirmed booking. It is recomputed when the
   * attendee's own address changes, too.
   */
  const guestListError = useMemo(() => {
    const primary = email.trim().toLowerCase()
    if (guests.some((guest) => !EMAIL_PATTERN.test(guest))) {
      return t?.error_guest_invalid || "Enter a valid email address for each guest."
    }
    if (primary && guests.includes(primary)) {
      return t?.error_guest_self || "That is already the attendee's email."
    }
    return ""
  }, [guests, email, t])

  const validate = () => {
    const errors = {}
    if (!name.trim()) errors.attendee_name = t?.error_name_required
    if (!email.trim()) errors.attendee_email = t?.error_email_required
    else if (!EMAIL_PATTERN.test(email.trim())) {
      errors.attendee_email = t?.error_email_invalid
    }
    if (!selectedSlot) errors.slot = t?.error_slot_required
    // Blocks confirmation: an unusable guest is a correction to make, not a
    // guest to quietly leave out of the meeting.
    if (guestListError) errors.guest_emails = guestListError
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  /**
   * Take the list as typed, normalized.
   *
   * Only an entry there is no room for is dropped — it was never added. An
   * address that is simply wrong is kept so the person can see and fix it;
   * `guestListError` then blocks confirmation until they do.
   */
  const changeGuests = (values) => {
    const next = []
    let overflow = ""
    values.forEach((raw) => {
      const candidate = String(raw).trim().toLowerCase()
      if (!candidate || next.includes(candidate)) return
      if (next.length >= GUEST_MAX) {
        overflow = t?.error_guests_too_many || `Add up to ${GUEST_MAX} additional guests.`
        return
      }
      next.push(candidate)
    })
    setGuests(next)
    setFieldErrors((previous) => ({ ...previous, guest_emails: overflow || undefined }))
  }

  /**
   * What a successful response actually says about the attempt.
   *
   * A 200 is not a confirmation: replaying a request whose booking was
   * cancelled in the meantime answers 200 with a cancelled record, and that is
   * a finished attempt, not a scheduled meeting. Only `confirmed` closes the
   * dialog; `cancelled` and `failed` end the attempt with a message; anything
   * else is still unresolved.
   */
  const resolveOutcome = (booking) => {
    if (booking?.status === "confirmed") {
      onBooked?.(booking)
      close(true)
      return true
    }
    if (booking?.status === "cancelled" || booking?.status === "failed") {
      setPending(false)
      setPendingBookingId("")
      setSubmitted(null)
      setRequestId("")
      setSubmitError(
        booking.status === "cancelled"
          ? t?.error_booking_cancelled || "That booking was cancelled."
          : t?.error_booking_failed || "We could not schedule the meeting."
      )
      return true
    }
    return false
  }

  const submit = async () => {
    if (submitting || pending || !validate()) return

    const id = requestId || newRequestId()
    if (!id) {
      setSubmitError(
        t?.error_booking_unavailable || "Online booking is unavailable in this browser."
      )
      return
    }
    setRequestId(id)
    setSubmitting(true)
    setSubmitError("")

    // Freeze exactly what is being sent. A later check must replay this, not
    // whatever the form happens to hold by then — the same request id with
    // different content is a conflict, not a recovery.
    const payload = {
      slotId: selectedSlot.id,
      slotVersion: selectedSlot.version,
      name: name.trim(),
      email: email.trim(),
      message: message.trim(),
      timezone,
      requestId: id,
      // Frozen with everything else: a readback replays these guests, and a
      // different list under the same id is a conflict, not a recovery.
      guestEmails: guests,
    }
    setSubmitted(payload)

    try {
      const response = await Scheduling.createBooking(payload)
      const booking = response?.data?.booking
      if (resolveOutcome(booking)) return
      // Unresolved: hold the same request and read it back.
      setPending(true)
      setPendingBookingId(booking?.id || "")
    } catch (error) {
      const parsed = parseSchedulingError(error)
      if (parsed.fieldErrors) {
        setFieldErrors(
          Object.fromEntries(
            Object.entries(parsed.fieldErrors).map(([field, value]) => [
              field,
              fieldErrorText(value),
            ])
          )
        )
      }
      if (["slot_unavailable", "slot_expired", "slot_changed"].includes(parsed.code)) {
        // The time went while the dialog was open: refresh and keep the details.
        setSelectedSlot(null)
        loadSlots()
      }
      if (PRE_BOOKING_CODES.includes(parsed.code)) {
        // Nothing can have been created, so this attempt is over and the next
        // submit is a new booking with its own request id.
        setSubmitted(null)
        setRequestId("")
      } else {
        // We never learned the outcome. Hold the attempt and offer the
        // readback; editing and resubmitting under the same id is a conflict.
        setPending(true)
      }
      setSubmitError(errorText(t, parsed, t?.error_booking_failed))
    } finally {
      setSubmitting(false)
    }
  }

  const checkStatus = async () => {
    setSubmitting(true)
    setSubmitError("")
    try {
      if (pendingBookingId) {
        // Read the record back rather than re-posting anything.
        const response = await Scheduling.getBooking(pendingBookingId, { refresh: true })
        resolveOutcome(response?.data?.booking)
        return
      }

      // No record to read yet: replay the frozen payload, unchanged.
      const response = await Scheduling.createBooking(submitted)
      const booking = response?.data?.booking
      if (!resolveOutcome(booking)) setPendingBookingId(booking?.id || "")
    } catch (error) {
      const parsed = parseSchedulingError(error)
      if (parsed.code === "booking_not_found" || PRE_BOOKING_CODES.includes(parsed.code)) {
        // The backend has finished with this attempt; a retry starts a new one.
        setPending(false)
        setPendingBookingId("")
        setSubmitted(null)
        setRequestId("")
      }
      setSubmitError(errorText(t, parsed, t?.error_booking_failed))
    } finally {
      setSubmitting(false)
    }
  }

  const close = (booked = false) => {
    if (booked || (!pending && !submitting)) {
      setName("")
      setEmail("")
      setMessage("")
      setSelectedSlot(null)
    }
    if (booked) {
      setPending(false)
      setPendingBookingId("")
      setSubmitted(null)
    }
    onClose?.()
  }

  const offsetLabel = zoneOffsetLabel(timezone)

  return (
    <Modal
      open={open}
      onCancel={() => close()}
      footer={null}
      width={760}
      centered
      destroyOnClose
      title={t?.book_title || "Schedule a meeting"}
      className="scheduling-modal"
    >
      <div className="sch-book-head">
        <span className="sch-muted">
          {t?.book_subtitle || "Google Meet"}
        </span>
        <Select
          className="sch-zone-select"
          showSearch
          value={timezone}
          disabled={pending || submitting}
          onChange={setTimezone}
          options={zones.map((zone) => ({ value: zone, label: zone }))}
        />
        <span className="sch-muted">{offsetLabel}</span>
      </div>

      {loadError ? (
        <Alert
          type="error"
          showIcon
          message={loadError}
          className="sch-alert"
          action={<button type="button" className="sch-btn" disabled={loadingSlots} onClick={loadSlots}>{t?.retry || "Retry"}</button>}
        />
      ) : null}

      {pending ? (
        <Alert
          type="warning"
          showIcon
          className="sch-alert"
          message={t?.booking_pending_title || "We are still confirming this booking"}
          description={t?.booking_pending_body || "Do not create another booking yet."}
          action={
            <button type="button" className="sch-btn" disabled={submitting} onClick={checkStatus}>
              {t?.check_status || "Check status"}
            </button>
          }
        />
      ) : null}

      <Spin spinning={loadingSlots}>
        <div className="sch-book-grid">
          <div>
            <p className="sch-label">{t?.choose_date || "Date"}</p>
            <div className="sch-date-list">
              {dates.length === 0 && !loadingSlots && !loadError ? (
                <p className="sch-muted">{t?.no_slots || "No published times are free."}</p>
              ) : null}
              {dates.map((date) => (
                <button
                  key={date}
                  type="button"
                  disabled={pending || submitting}
                  className={`sch-date-chip${date === selectedDate ? " is-selected" : ""}`}
                  onClick={() => setSelectedDate(date)}
                >
                  {formatCalendarDate(date, lang)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="sch-label">{t?.choose_time || "Time"}</p>
            <div className="sch-slot-grid">
              {daySlots.map((slot) => (
                <button
                  key={slot.id}
                  type="button"
                  aria-pressed={selectedSlot?.id === slot.id}
                  disabled={pending || submitting}
                  className={`sch-slot${selectedSlot?.id === slot.id ? " is-selected" : ""}`}
                  onClick={() => setSelectedSlot(slot)}
                >
                  {formatTime(slot.start_at, timezone, lang)}
                </button>
              ))}
            </div>
            {fieldErrors.slot ? <p className="sch-field-error">{fieldErrors.slot}</p> : null}

            {selectedSlot ? (
              <p className="sch-chosen">
                <span dir="ltr">
                  {formatTimeRange(selectedSlot.start_at, selectedSlot.end_at, timezone, lang)}
                </span>{" "}
                · {timezone} {offsetLabel} ·{" "}
                {`${selectedSlot.duration_minutes} ${t?.minutes || "minutes"}`}
              </p>
            ) : null}

            <div className="sch-field">
              <label className="sch-label" htmlFor="sch-name">
                {t?.attendee_name || "Attendee name"} *
              </label>
              <Input
                id="sch-name"
                value={name}
                maxLength={120}
                disabled={pending || submitting}
                status={fieldErrors.attendee_name ? "error" : ""}
                onChange={(event) => setName(event.target.value)}
              />
              {fieldErrors.attendee_name ? (
                <p className="sch-field-error">{fieldErrors.attendee_name}</p>
              ) : null}
            </div>

            <div className="sch-field">
              <label className="sch-label" htmlFor="sch-email">
                {t?.attendee_email || "Attendee email"} *
              </label>
              <Input
                id="sch-email"
                dir="ltr"
                value={email}
                maxLength={254}
                disabled={pending || submitting}
                status={fieldErrors.attendee_email ? "error" : ""}
                onChange={(event) => setEmail(event.target.value)}
              />
              {fieldErrors.attendee_email ? (
                <p className="sch-field-error">{fieldErrors.attendee_email}</p>
              ) : null}
            </div>

            <div className="sch-field">
              <label className="sch-label" htmlFor="sch-guests">
                {t?.guest_emails || "Additional guests"}
              </label>
              <Select
                id="sch-guests"
                mode="tags"
                className="sch-select"
                dir="ltr"
                value={guests}
                disabled={pending || submitting}
                status={fieldErrors.guest_emails || guestListError ? "error" : ""}
                tokenSeparators={[",", " ", ";"]}
                placeholder={t?.guest_emails_placeholder || "name@company.com"}
                onChange={changeGuests}
              />
              {fieldErrors.guest_emails || guestListError ? (
                <p className="sch-field-error">
                  {fieldErrors.guest_emails || guestListError}
                </p>
              ) : (
                <p className="sch-hint">
                  {t?.guest_emails_hint ||
                    `Up to ${GUEST_MAX}. They are invited to the same meeting and can see each other.`}
                </p>
              )}
            </div>

            <div className="sch-field">
              <label className="sch-label" htmlFor="sch-message">
                {t?.attendee_message || "Message"}
              </label>
              <TextArea
                id="sch-message"
                rows={3}
                maxLength={2000}
                disabled={pending || submitting}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
              />
            </div>

            <p className="sch-note">
              {t?.admin_booking_note ||
                "Only published, currently free times can be chosen. Calendar conflicts cannot be overridden here."}
            </p>
          </div>
        </div>
      </Spin>

      {submitError ? (
        <Alert type="error" showIcon message={submitError} className="sch-alert" />
      ) : null}

      <div className={`sch-modal-actions${isRTL ? " is-rtl" : ""}`}>
        <button type="button" className="sch-btn sch-btn-secondary" onClick={() => close()}>
          {t?.cancel || "Cancel"}
        </button>
        <button
          type="button"
          className="sch-btn sch-btn-primary"
          disabled={
            submitting ||
            pending ||
            loadingSlots ||
            Boolean(loadError) ||
            !selectedSlot ||
            Boolean(guestListError)
          }
          onClick={submit}
        >
          {submitting ? t?.confirming || "Confirming…" : t?.confirm_meeting || "Confirm meeting"}
        </button>
      </div>
    </Modal>
  )
}

export default BookMeetingModal
